import { apiFetch } from '../auth/apiFetch.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Revision } from './useRevisions.ts';

// Morphology / hardware-revision catalog admin (PRD-0007 Phase C). Tenant-scoped
// on the server; these hooks drive the Catalog workspace.

export interface Morphology {
  morphology_id: string;
  name: string;
  description: string | null;
  created_at: string;
}

export type ContainerRole = 'bootstrap' | 'update' | 'both';

export interface Container {
  id: number;
  version_id: number;
  name: string;
  role: ContainerRole;
  ordinal: number;
  value: string | null; // the pinned image ref; null until set (PRD-0008)
  meta: Record<string, unknown>;
}

// A revision plus derived flags: `complete` (PRD-0008: has containers and all
// valued) and `bringup_ready` (PRD-0009: complete AND a bringup script is set).
export interface CatalogVersion extends Revision {
  complete: boolean;
  script_key: string | null;
  bringup_ready: boolean;
}

// A revision's manifest read: its containers + whether they're all valued.
export interface Manifest {
  containers: Container[];
  complete: boolean;
}

async function readJson(r: Response): Promise<unknown> {
  if (!r.ok) {
    const detail = (await r.json().catch(() => null)) as { detail?: unknown } | null;
    const msg =
      typeof detail?.detail === 'string' ? detail.detail : `status ${r.status}`;
    throw new Error(msg);
  }
  return r.json();
}

// -- morphologies -------------------------------------------------------------

export function useMorphologies() {
  return useQuery({
    queryKey: ['morphologies'] as const,
    queryFn: async (): Promise<Morphology[]> => {
      const body = (await readJson(await apiFetch('/api/morphologies'))) as {
        morphologies?: Morphology[];
      };
      return body.morphologies ?? [];
    },
  });
}

export interface NewMorphology {
  morphology_id: string;
  name: string;
  description?: string | undefined;
}

export function useCreateMorphology() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewMorphology): Promise<unknown> =>
      apiFetch('/api/morphologies', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }).then(readJson),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['morphologies'] }),
  });
}

// -- revisions ----------------------------------------------------------------

export function useVersions(morphology: string | undefined) {
  return useQuery({
    queryKey: ['catalog-versions', morphology] as const,
    enabled: !!morphology,
    queryFn: async (): Promise<CatalogVersion[]> => {
      const body = (await readJson(
        await apiFetch(
          `/api/morphologies/${encodeURIComponent(morphology as string)}/versions`,
        ),
      )) as { versions?: CatalogVersion[] };
      return body.versions ?? [];
    },
  });
}

export interface NewVersion {
  version: string;
  notes?: string | undefined;
}

export function useCreateVersion(morphology: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewVersion): Promise<unknown> =>
      apiFetch(`/api/morphologies/${encodeURIComponent(morphology)}/versions`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }).then(readJson),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['catalog-versions', morphology] }),
  });
}

// -- container manifest -------------------------------------------------------

export function useContainers(
  morphology: string | undefined,
  version: string | undefined,
) {
  return useQuery({
    queryKey: ['catalog-containers', morphology, version] as const,
    enabled: !!morphology && !!version,
    queryFn: async (): Promise<Manifest> => {
      const body = (await readJson(
        await apiFetch(
          `/api/morphologies/${encodeURIComponent(morphology as string)}` +
            `/versions/${encodeURIComponent(version as string)}/containers`,
        ),
      )) as { containers?: Container[]; complete?: boolean };
      return { containers: body.containers ?? [], complete: body.complete ?? false };
    },
  });
}

export interface ManifestValue {
  id: number;
  value: string;
}

// Save a revision's container image values as a unit (PRD-0008). The server
// rejects (400) unless every container ends up valued; on success invalidates
// the manifest and the versions list (its `complete` flag changes).
export function useSaveManifest(morphology: string, version: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (containers: ManifestValue[]): Promise<unknown> =>
      apiFetch(
        `/api/morphologies/${encodeURIComponent(morphology)}` +
          `/versions/${encodeURIComponent(version)}/manifest`,
        {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ containers }),
        },
      ).then(readJson),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['catalog-containers', morphology, version],
      });
      void qc.invalidateQueries({ queryKey: ['catalog-versions', morphology] });
    },
  });
}

export interface NewContainer {
  name: string;
  role: ContainerRole;
  ordinal?: number | undefined;
}

export function useAddContainer(morphology: string, version: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewContainer): Promise<unknown> =>
      apiFetch(
        `/api/morphologies/${encodeURIComponent(morphology)}` +
          `/versions/${encodeURIComponent(version)}/containers`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(input),
        },
      ).then(readJson),
    onSuccess: () =>
      void qc.invalidateQueries({
        queryKey: ['catalog-containers', morphology, version],
      }),
  });
}

export function useRemoveContainer(morphology: string, version: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (containerId: number): Promise<void> => {
      const r = await apiFetch(
        `/api/morphologies/${encodeURIComponent(morphology)}` +
          `/versions/${encodeURIComponent(version)}/containers/${containerId}`,
        { method: 'DELETE' },
      );
      if (!r.ok && r.status !== 204) {
        const detail = (await r.json().catch(() => null)) as {
          detail?: unknown;
        } | null;
        throw new Error(
          typeof detail?.detail === 'string' ? detail.detail : `status ${r.status}`,
        );
      }
    },
    onSuccess: () =>
      void qc.invalidateQueries({
        queryKey: ['catalog-containers', morphology, version],
      }),
  });
}
